import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { generateUniqueId, getCookie } from "utils/utils";

interface WebSocketContextProps {
  message: string | any | null;
  sendMessage: (msg: string) => void;
  clearMessage: () => void;
}

const WebSocketContext = createContext<WebSocketContextProps | undefined>(
  undefined
);

const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [message, setMessage] = useState<string | object | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef<number>(0);

  const connectWebSocket = useCallback(() => {
    const sessionId = getCookie("session_id");

    const newSessionId = generateUniqueId();
    if (!sessionId){
      document.cookie = `session_id=${newSessionId}; path=/;`;
    }

    const socketUrl = `${process.env.REACT_APP_SOCKET_URL}${sessionId ?? newSessionId}/`;
    socketRef.current = new WebSocket(socketUrl);

    socketRef.current.addEventListener("open", () => {
      reconnectAttempts.current = 0;

      // Send heartbeat to keep connection alive
      heartbeatRef.current = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({ type: "ping" }));
        }
      }, 10000);
    });

    socketRef.current.addEventListener("message", (event) => {
      try {
        const parsedData = JSON.parse(event.data);
        setMessage(parsedData);
      } catch (error) {
        setMessage(event.data);
      }
    });

    socketRef.current.addEventListener("close", () => {
      clearInterval(heartbeatRef.current as NodeJS.Timeout);
      heartbeatRef.current = null;
      attemptReconnect();
    });

    socketRef.current.addEventListener("error", (error) => {
      console.error("WebSocket error:", error);
    });
  }, []);

  const attemptReconnect = useCallback(() => {
    if (reconnectAttempts.current >= 5) {
      return;
    }

    const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 30000); // Exponential backoff (max 30s)
    reconnectAttempts.current += 1;

    reconnectTimeoutRef.current = setTimeout(() => {
      connectWebSocket();
    }, delay);
  }, [connectWebSocket]);

  useEffect(() => {
    connectWebSocket();

    // Reconnect on network recovery
    const handleOnline = () => {
      connectWebSocket();
    };
    window.addEventListener("online", handleOnline);

    return () => {
      socketRef.current?.close();
      clearInterval(heartbeatRef.current as NodeJS.Timeout);
      clearTimeout(reconnectTimeoutRef.current as NodeJS.Timeout);
      window.removeEventListener("online", handleOnline);
    };
  }, [connectWebSocket]);

  const sendMessage = useCallback((msg: string) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(msg);
    }
  }, []);

  const clearMessage = useCallback(() => {
    setMessage(null);
  }, []);

  const contextValue = useMemo(
    () => ({ message, sendMessage, clearMessage }),
    [message, sendMessage, clearMessage]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;

export const useWebSocket = () => {
  const context = React.useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
